/* eslint-disable react/no-danger */
import { isStandalone } from '@digital-retail/store-manager';
import _ from 'lodash';
import getConfig from 'next/config';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import constants from '../../../config/constants';
import defaultConfig from '../../../config/url/default.config';
import { RatingAndReviews } from '../../../external_dependencies/ratingAndReviews/RatingAndReviews';
import { withApplicationContext } from '../../../utils/ApplicationContext';
import { withDigitalData } from '../../../utils/DigitalDataContext';
import { withLabels } from '../../../utils/LabelsContext';
import Tooltip, { TooltipText } from '../../ui/Tooltip/Tooltip';
import { getSellerClass, getSellerLink, getTooltipText, validateSellerName } from '../helpers';
import { SellerInfoMobileStyle } from './SellerInfoMobile.style';

const { publicRuntimeConfig } = getConfig();

const SellerInfoMobile = ({ product, labels, appCtx, digitalData, ratings, template }) => {
  const { regionCode, store } = appCtx;
  const isSellerPopupEnabled = _.get(appCtx, 'siteConfig.toggles.isSellerPopupEnabled', false);
  const { ENVIRONMENT } = publicRuntimeConfig;
  const { currentVariant, variants } = product;
  const variant = variants.find((item) => item.id === currentVariant);
  const sellerId = _.get(variant, 'offerings[0].sellerId', '');
  const sellerName = _.get(variant, 'offerings[0].sellerName', '');
  const sellerNameLower = sellerName && sellerName.toLowerCase();
  const tooltipRef = useRef();
  const isStandaloneStore = isStandalone({ store });
  const showSellerStores = ['fa_america', 'fa_carters', 'fa_aldo', 'fa_crabarr', 'fa_thedrop'];
  const shouldShowSellers = showSellerStores.includes(store);

  const rcClass = `reviews-column${ratings ? '' : '-hidden'}`; // Don't change this logic, in order to update reviews we need the section to render so data loads

  useEffect(() => {
    digitalData.pdp.setSoldBy(sellerNameLower);
  }, [sellerNameLower]);

  const shouldShowSellerInfo = _.get(appCtx, 'siteConfig.toggles.isSellerInfoVisibleInPDP', false);
  const isSISExperienceOn = _.get(appCtx, 'siteConfig.toggles.isSISExperienceOn', false);

  if (!sellerName || !sellerId || !shouldShowSellerInfo) {
    return null;
  }
  const sellerClass = getSellerClass({ sellerNameLower, constants });
  const sellerRedirectionMap = _.get(
    appCtx,
    'siteConfig.configurations.sellerRedirectionMap',
    defaultConfig.SELLER_REDIRECTION_MAP[ENVIRONMENT][regionCode]
  );
  const { isOutOfStock } = product;
  const validatedSellerName = validateSellerName(sellerNameLower, regionCode);

  return !shouldShowSellers ? (
    <div
      className={`borderContainer rebranded ${isOutOfStock && 'out-of-stock'} ${
        !isStandaloneStore ? 'no-border-top' : ''
      }`}
    >
      <div className="brand-and-ratings">
        <div className="sellerInfoContainer fa--seller-info__mobile">
          <span>{`${labels.SELLER_SOLD_BY} `}</span>
          {sellerClass ? <span className={`${sellerClass}Icon`} /> : null}
          <a
            id="testId-SellerInfo-sellerName"
            href={getSellerLink({
              sellerClass,
              regionCode,
              sellerName: sellerClass === 'homecenter' ? validatedSellerName.toUpperCase() : sellerName,
              env: ENVIRONMENT,
              isSISExperienceOn,
              sellerRedirectionMap,
              store,
            })}
            className="link"
          >
            <span className="underline">{validatedSellerName}</span>
          </a>
          {isSellerPopupEnabled && (
            <Tooltip
              position="bottom-left-1"
              hasCaret
              tooltipContent={
                <TooltipText
                  text={
                    <div
                      className="sellerTooltipText"
                      dangerouslySetInnerHTML={{
                        __html: getTooltipText({
                          sellerName: sellerClass || sellerNameLower,
                          labels,
                          storePlaceholderText: constants.STORE_PLACEHOLDER,
                          sellerData: product.sellerInfo,
                          isNational: !_.get(product, 'internationalShipping.applicable', false),
                        }),
                      }}
                    />
                  }
                  viewType="fullWidth"
                />
              }
              viewType="fullWidth"
              tooltipContentRef={tooltipRef}
            >
              <i ref={tooltipRef} id="seller-tooltip-icon" className="csicon-alert alertIcon">
                <span className="sr-only">Seller Info</span>
              </i>
            </Tooltip>
          )}
        </div>

        {!isStandaloneStore && (
          <div className={`${rcClass} fa--reviews-${template || 'softline'}__mobile`}>
            <RatingAndReviews />
          </div>
        )}
      </div>
      <style jsx>{SellerInfoMobileStyle}</style>
    </div>
  ) : null;
};

SellerInfoMobile.defaultProps = {
  template: 'softline',
  ratings: null,
};

SellerInfoMobile.propTypes = {
  product: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  appCtx: PropTypes.object.isRequired,
  digitalData: PropTypes.object.isRequired,
  ratings: PropTypes.object,
  template: PropTypes.string,
};

export default withApplicationContext(withLabels(withDigitalData(SellerInfoMobile)));
